import calculateTickerOverview from './calculateTickerOverview';
import { OPTIONS_PER_CONTRACT } from 'utils/shared/quantlib/utils';
import isBlock from './isBlock';
import isGoldenSweep from './isGoldenSweep';
import isSplit from './isSplit';
import isSweep from './isSweep';
import isUnusual from './isUnusual';
import {
  DEFAULT_PUT_FLOW_BASIS,
  getDefaultFlowSideBasisSelection,
  type FlowSideBasisSelection,
  getFlowBasisQueryOverrides,
  normalizeFlowSideBasisSelection,
  type PutFlowBasis,
} from './presets';
import { mapCondensedSectors } from './sectors';
import { CallPut, OptionFlowEnrichmentFields, Trade, TradeDirection } from './types';
import { calculateFlowSentimentBreakdown } from './flowSentiment';
import { getDirectionalFlowSign, getTradePositionSign } from './flowDirection';
import { hasSolvedGreeks } from './hasSolvedGreeks';
import { normalizeCallPut } from './normalizeCallPut';
import { getTradeDirectionFromTradeSide } from './tradeSide';

export interface OptionFlowAggregateOptions {
  flowSideBasis?: FlowSideBasisSelection;
  putFlowBasis?: PutFlowBasis;
}

export interface OptionFlowPutFlowAggregate {
  flowSideBasis: FlowSideBasisSelection;
  putFlowBasis: PutFlowBasis;
  tradeCount: number;
  putFlow: number;
  putCallRatio: number;
  putFlowVolume: number;
  putFlowPremium: number;
  putVolume: number;
  callVolume: number;
  putPremium: number;
  callPremium: number;
}

export interface OptionFlowTickerAggregate {
  ticker: string;
  tickerName: string;
  marketCap: number;
  sector: string;
  tradeCount: number;
  putFlow: number;
  putCallRatio: number;
  putFlowVolume: number;
  putFlowPremium: number;
  putFlowBasis: PutFlowBasis;
  flowSideBasis: FlowSideBasisSelection;
  otm: number;
  momentum: number;
  volumeGrowth: number;
  contracts: number;
  premium: number;
  price: number;
  expiry: number;
  unusualOtm: number;
  unusualContracts: number;
  unusualPremium: number;
  sweepOtm: number;
  sweepContracts: number;
  sweepPremium: number;
  putVolume: number;
  callVolume: number;
  putPremium: number;
  callPremium: number;
  bullishPremium: number;
  bearishPremium: number;
  neutralPremium: number;
  sentimentScore: number;
  directionalContracts: number;
  neutralContracts: number;
  directionalCoveragePercent: number | null;
  /**
   * Contract-weighted enrichment and signed exposures. Only the CSV export reads these,
   * and the aggregate memo re-runs on every realtime feed flush, so they are computed on
   * first access and cached rather than on every rebuild.
   */
  getEnrichmentAggregates: () => OptionFlowEnrichmentAggregates;
  netTradeCount: number;
  netContracts: number;
  netPremium: number;
  netUnusualContracts: number;
  netUnusualPremium: number;
  netSweepContracts: number;
  netSweepPremium: number;
  netSplitContracts: number;
  netSplitPremium: number;
  netBlockContracts: number;
  netBlockPremium: number;
  netGoldenSweepContracts: number;
  netGoldenSweepPremium: number;
  netPremiumShare: number;
  splitContracts: number;
  splitPremium: number;
  splitOtm: number;
  blockContracts: number;
  blockPremium: number;
  blockOtm: number;
  goldenSweepContracts: number;
  goldenSweepPremium: number;
  goldenSweepOtm: number;
  hasTopPosition: boolean;
  hasHighHeat: boolean;
  hasAboveAsk: boolean;
  hasBelowBid: boolean;
}

export interface OptionFlowSectorAggregate {
  sector: string;
  putFlow: number;
  otm: number;
  contracts: number;
  premium: number;
  expiry: number;
  unusualOtm: number;
  unusualContracts: number;
  unusualPremium: number;
  sweepOtm: number;
  sweepContracts: number;
  sweepPremium: number;
  splitOtm: number;
  splitContracts: number;
  splitPremium: number;
  blockOtm: number;
  blockContracts: number;
  blockPremium: number;
  goldenSweepOtm: number;
  goldenSweepContracts: number;
  goldenSweepPremium: number;
  bullishPremium: number;
  bearishPremium: number;
  neutralPremium: number;
  sentimentScore: number;
  netPremium: number;
  netPremiumShare: number;
}

export interface SectorOptionFlowAggregateOptions extends OptionFlowAggregateOptions {
  shouldIncludeSector?: (sector: string, trade: Trade) => boolean;
}

const HIGH_HEAT_SCORE_THRESHOLD = 18;
const NEUTRAL_PUT_FLOW = 0.5;

const getBasis = ({ flowSideBasis, putFlowBasis }: OptionFlowAggregateOptions = {}) => ({
  flowSideBasis: flowSideBasis || getDefaultFlowSideBasisSelection(),
  flowSideSelection: normalizeFlowSideBasisSelection(flowSideBasis),
  putFlowBasis: putFlowBasis || DEFAULT_PUT_FLOW_BASIS,
});

const toPercent = (value: number) => Math.round(value * 100);

// A midpoint print carries no directional buy/sell read. It's identified by either
// the fine-grained trade direction or the coarse trade side being Mid.
const isMidpointTrade = (trade: Trade): boolean =>
  (trade.tradeDirection || getTradeDirectionFromTradeSide(trade.tradeSide)) === TradeDirection.Mid;

const getRoundedOtm = (trades: Trade[]) => Math.round(calculateTickerOverview(trades).otm);

type EnrichedTrade = Trade & OptionFlowEnrichmentFields;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

export interface OptionFlowEnrichmentAggregates {
  uniqueContracts: number;
  avgImpliedVol: number | null;
  avgNbboMidIv: number | null;
  avgProbabilityOfTouch: number | null;
  avgRiskFreeRate: number | null;
  grossDeltaAdjustedNotional: number | null;
  netDeltaAdjustedNotional: number | null;
  netDeltaShares: number | null;
  netGammaExposure: number | null;
  netThetaExposure: number | null;
  netVegaExposure: number | null;
  netVannaExposure: number | null;
  netCharmExposure: number | null;
}

interface WeightedAccumulator {
  total: number;
  contracts: number;
}

interface SignedAccumulator {
  total: number;
  hasData: boolean;
}

const finishWeighted = (accumulator: WeightedAccumulator): number | null =>
  accumulator.contracts > 0 ? accumulator.total / accumulator.contracts : null;

const finishSigned = (accumulator: SignedAccumulator): number | null =>
  accumulator.hasData ? accumulator.total : null;

const addWeighted = (accumulator: WeightedAccumulator, value: unknown, contracts: number) => {
  const number = toFiniteNumber(value);

  if (number === undefined || contracts <= 0) {
    return;
  }

  accumulator.total += number * contracts;
  accumulator.contracts += contracts;
};

const addSigned = (
  accumulator: SignedAccumulator,
  perContractExposure: number | undefined,
  contracts: number | undefined,
  sign: number,
) => {
  if (perContractExposure === undefined || contracts === undefined) {
    return;
  }

  accumulator.hasData = true;
  accumulator.total += perContractExposure * contracts * sign;
};

/**
 * Weighted averages, enrichment coverage, unique contracts, and every signed exposure
 * in a SINGLE pass. This runs inside the realtime flow page's aggregate memo on every
 * feed flush, so each extra walk of the ticker's trades costs frame budget directly.
 */
const calculateEnrichmentAggregates = (trades: EnrichedTrade[]): OptionFlowEnrichmentAggregates => {
  const contractKeys = new Set<string>();
  const impliedVol: WeightedAccumulator = { total: 0, contracts: 0 };
  const nbboMidIv: WeightedAccumulator = { total: 0, contracts: 0 };
  const probabilityOfTouch: WeightedAccumulator = { total: 0, contracts: 0 };
  const riskFreeRate: WeightedAccumulator = { total: 0, contracts: 0 };
  const deltaShares: SignedAccumulator = { total: 0, hasData: false };
  const deltaNotional: SignedAccumulator = { total: 0, hasData: false };
  const gammaExposure: SignedAccumulator = { total: 0, hasData: false };
  const thetaExposure: SignedAccumulator = { total: 0, hasData: false };
  const vegaExposure: SignedAccumulator = { total: 0, hasData: false };
  const vannaExposure: SignedAccumulator = { total: 0, hasData: false };
  const charmExposure: SignedAccumulator = { total: 0, hasData: false };
  let grossNotional = 0;
  let hasGrossNotional = false;

  for (let i = 0; i < trades.length; i++) {
    const trade = trades[i];
    const contracts = toFiniteNumber(trade.size);
    const weight = Math.max(0, contracts ?? 0);
    const sign = getTradePositionSign(trade);
    const spot = toFiniteNumber(trade.spot) ?? toFiniteNumber(trade.priceExternalApi);
    // A row the producer could not price carries 0 across implied vol and every
    // greek. Averaging or summing those zeros understates the ticker: they land
    // on the largest, deepest-in-the-money prints. Skip them the same way a null
    // is skipped, so a ticker with nothing priced reports blank, not zero.
    const solved = hasSolvedGreeks(trade);
    const delta = solved ? toFiniteNumber(trade.delta) : undefined;
    const gamma = solved ? toFiniteNumber(trade.gamma) : undefined;
    const theta = solved ? toFiniteNumber(trade.theta) : undefined;
    const vega = solved ? toFiniteNumber(trade.vega) : undefined;
    const vanna = solved ? toFiniteNumber(trade.vanna) : undefined;
    const charm = solved ? toFiniteNumber(trade.charm) : undefined;

    addWeighted(impliedVol, solved ? trade.impliedVol : undefined, weight);
    addWeighted(nbboMidIv, trade.nbboMidIv, weight);
    addWeighted(probabilityOfTouch, trade.probTouch, weight);
    addWeighted(riskFreeRate, trade.riskFreeRate, weight);

    addSigned(
      deltaShares,
      delta === undefined ? undefined : delta * OPTIONS_PER_CONTRACT,
      contracts,
      sign,
    );
    addSigned(
      deltaNotional,
      delta === undefined || spot === undefined ? undefined : delta * OPTIONS_PER_CONTRACT * spot,
      contracts,
      sign,
    );
    addSigned(
      gammaExposure,
      gamma === undefined || spot === undefined
        ? undefined
        : gamma * OPTIONS_PER_CONTRACT * spot * spot * 0.01,
      contracts,
      sign,
    );
    addSigned(
      thetaExposure,
      theta === undefined ? undefined : theta * OPTIONS_PER_CONTRACT,
      contracts,
      sign,
    );
    addSigned(
      vegaExposure,
      vega === undefined ? undefined : vega * OPTIONS_PER_CONTRACT,
      contracts,
      sign,
    );
    addSigned(
      vannaExposure,
      vanna === undefined ? undefined : vanna * OPTIONS_PER_CONTRACT,
      contracts,
      sign,
    );
    addSigned(
      charmExposure,
      charm === undefined ? undefined : charm * OPTIONS_PER_CONTRACT,
      contracts,
      sign,
    );

    if (delta !== undefined && contracts !== undefined && spot !== undefined) {
      hasGrossNotional = true;
      grossNotional += Math.abs(delta * contracts * OPTIONS_PER_CONTRACT * spot);
    }

    const strike = toFiniteNumber(trade.strike);
    const cp = normalizeCallPut(trade.cp);
    const expiry =
      trade.expireYear != null && trade.expireMonth != null && trade.expireDay != null
        ? `${trade.expireYear}-${trade.expireMonth}-${trade.expireDay}`
        : trade.expiryString;

    if (strike !== undefined && cp && expiry) {
      contractKeys.add(`${expiry}|${strike}|${cp}`);
    }
  }

  return {
    uniqueContracts: contractKeys.size,
    avgImpliedVol: finishWeighted(impliedVol),
    avgNbboMidIv: finishWeighted(nbboMidIv),
    avgProbabilityOfTouch: finishWeighted(probabilityOfTouch),
    avgRiskFreeRate: finishWeighted(riskFreeRate),
    grossDeltaAdjustedNotional: hasGrossNotional ? grossNotional : null,
    netDeltaAdjustedNotional: finishSigned(deltaNotional),
    netDeltaShares: finishSigned(deltaShares),
    netGammaExposure: finishSigned(gammaExposure),
    netThetaExposure: finishSigned(thetaExposure),
    netVegaExposure: finishSigned(vegaExposure),
    netVannaExposure: finishSigned(vannaExposure),
    netCharmExposure: finishSigned(charmExposure),
  };
};

export const EMPTY_ENRICHMENT_AGGREGATES: OptionFlowEnrichmentAggregates = {
  uniqueContracts: 0,
  avgImpliedVol: null,
  avgNbboMidIv: null,
  avgProbabilityOfTouch: null,
  avgRiskFreeRate: null,
  grossDeltaAdjustedNotional: null,
  netDeltaAdjustedNotional: null,
  netDeltaShares: null,
  netGammaExposure: null,
  netThetaExposure: null,
  netVegaExposure: null,
  netVannaExposure: null,
  netCharmExposure: null,
};

const createEnrichmentAggregatesGetter = (
  trades: EnrichedTrade[],
): (() => OptionFlowEnrichmentAggregates) => {
  let cached: OptionFlowEnrichmentAggregates | undefined;

  return () => {
    if (!cached) {
      cached = calculateEnrichmentAggregates(trades);
    }

    return cached;
  };
};

export interface DirectionalFlowValue {
  bullish: number;
  bearish: number;
  neutral: number;
  directionalTotal: number;
  net: number;
}

export interface DirectionalFlowTotals {
  count: DirectionalFlowValue;
  contracts: DirectionalFlowValue;
  premium: DirectionalFlowValue;
}

const createDirectionalFlowValue = (): DirectionalFlowValue => ({
  bullish: 0,
  bearish: 0,
  neutral: 0,
  directionalTotal: 0,
  net: 0,
});

const addDirectionalFlowValue = (acc: DirectionalFlowValue, sign: -1 | 0 | 1, value: number) => {
  if (sign > 0) {
    acc.bullish += value;
  } else if (sign < 0) {
    acc.bearish += value;
  } else {
    acc.neutral += value;
  }

  acc.directionalTotal = acc.bullish + acc.bearish;
  acc.net = acc.bullish - acc.bearish;
};

export const getOptionFlowDirectionalSign = (trade: Trade): -1 | 0 | 1 =>
  getDirectionalFlowSign(trade);

export const calculateDirectionalFlowValue = (
  trades: Trade[],
  getValue: (trade: Trade) => number,
): DirectionalFlowValue =>
  trades.reduce<DirectionalFlowValue>((acc, trade) => {
    const value = getValue(trade) || 0;
    const sign = getOptionFlowDirectionalSign(trade);

    addDirectionalFlowValue(acc, sign, value);
    return acc;
  }, createDirectionalFlowValue());

export const calculateDirectionalFlowTotals = (trades: Trade[]): DirectionalFlowTotals =>
  trades.reduce<DirectionalFlowTotals>(
    (acc, trade) => {
      const sign = getOptionFlowDirectionalSign(trade);

      addDirectionalFlowValue(acc.count, sign, 1);
      addDirectionalFlowValue(acc.contracts, sign, trade.size || 0);
      addDirectionalFlowValue(acc.premium, sign, trade.premium || 0);

      return acc;
    },
    {
      count: createDirectionalFlowValue(),
      contracts: createDirectionalFlowValue(),
      premium: createDirectionalFlowValue(),
    },
  );

export const calculateDirectionalPremiumShare = (directionalPremium: DirectionalFlowValue) =>
  directionalPremium.directionalTotal === 0
    ? 0
    : directionalPremium.net / directionalPremium.directionalTotal;

export const getFlowAggregateOptionsFromSearchParams = (
  searchParams: URLSearchParams,
): OptionFlowAggregateOptions =>
  getFlowBasisQueryOverrides({
    flowSideBasis: searchParams.get('flowSideBasis'),
    flowSide: searchParams.get('flowSide'),
    side: searchParams.get('side'),
    putFlowBasis: searchParams.get('putFlowBasis'),
    flowMetricBasis: searchParams.get('flowMetricBasis'),
    metricBasis: searchParams.get('metricBasis'),
  });

export const getTradeMatchesFlowSideBasis = (
  trade: Trade,
  flowSideBasis: FlowSideBasisSelection = getDefaultFlowSideBasisSelection(),
): boolean => {
  const flowSideSelection = normalizeFlowSideBasisSelection(flowSideBasis);

  if (flowSideSelection.length > 1) {
    return flowSideSelection.some((side) => getTradeMatchesFlowSideBasis(trade, side));
  }

  const activeFlowSideBasis = flowSideSelection[0];
  const tradeDirection = trade.tradeDirection || getTradeDirectionFromTradeSide(trade.tradeSide);

  // "All" is the full feed, including non-directional midpoint prints, so aggregate
  // totals (contracts/premium) stay consistent with the raw trade table. Directional
  // metrics (net, put/call flow) drop mids at their own call sites instead.
  if (activeFlowSideBasis === 'all') {
    return true;
  }

  if (activeFlowSideBasis === 'aboveAsk') {
    return tradeDirection === TradeDirection.AboveAsk;
  }

  if (activeFlowSideBasis === 'mid') {
    return tradeDirection === TradeDirection.Mid;
  }

  if (activeFlowSideBasis === 'belowBid') {
    return tradeDirection === TradeDirection.BelowBid;
  }

  if (activeFlowSideBasis === 'bid') {
    return tradeDirection === TradeDirection.Bid;
  }

  return tradeDirection === TradeDirection.Ask;
};

export const filterTradesByFlowSideBasis = (
  trades: Trade[],
  flowSideBasis: FlowSideBasisSelection = getDefaultFlowSideBasisSelection(),
) => trades.filter((trade) => getTradeMatchesFlowSideBasis(trade, flowSideBasis));

export const calculateOptionFlowAggregate = (
  trades: Trade[],
  options: OptionFlowAggregateOptions = {},
): OptionFlowPutFlowAggregate => {
  const { flowSideBasis, flowSideSelection, putFlowBasis } = getBasis(options);
  const basisTrades = filterTradesByFlowSideBasis(trades, flowSideSelection);
  // Put/Call flow is a directional read of buy- vs sell-pressure, so non-directional
  // midpoint prints never count toward the split even when the active side selection
  // includes them (e.g. "All", or an Ask + Mid combination).
  const directionalTrades = basisTrades.filter((trade) => !isMidpointTrade(trade));
  let putPremium = 0;
  let callPremium = 0;
  let putVolume = 0;
  let callVolume = 0;

  directionalTrades.forEach((trade) => {
    const cp = normalizeCallPut(trade.cp);

    if (cp === CallPut.Put) {
      putPremium += trade.premium || 0;
      putVolume += trade.size || 0;
    }

    if (cp === CallPut.Call) {
      callPremium += trade.premium || 0;
      callVolume += trade.size || 0;
    }
  });

  const premiumTotal = putPremium + callPremium;
  const volumeTotal = putVolume + callVolume;
  const putFlowPremium = premiumTotal === 0 ? 0 : putPremium / premiumTotal;
  const putFlowVolume = volumeTotal === 0 ? 0 : putVolume / volumeTotal;
  const selectedPutFlow = putFlowBasis === 'premium' ? putFlowPremium : putFlowVolume;
  // With no directional prints (e.g. a mid-only selection) there is no put/call
  // signal, so report a neutral 50/50 read rather than a misleading 0%.
  const putFlow = directionalTrades.length === 0 ? NEUTRAL_PUT_FLOW : selectedPutFlow;

  return {
    flowSideBasis,
    putFlowBasis,
    tradeCount: basisTrades.length,
    putFlow,
    putCallRatio: putFlowVolume,
    putFlowVolume,
    putFlowPremium,
    putVolume,
    callVolume,
    putPremium,
    callPremium,
  };
};

export const buildTickerOptionFlowAggregates = (
  trades: Trade[],
  dailyVolumeMap: Record<string, number> = {},
  options: OptionFlowAggregateOptions = {},
) => {
  const { flowSideBasis, flowSideSelection, putFlowBasis } = getBasis(options);
  const basisTrades = filterTradesByFlowSideBasis(trades, flowSideSelection);
  const tradesByTicker = basisTrades.reduce<Record<string, Trade[]>>((acc, trade) => {
    if (!trade.ticker) {
      return acc;
    }

    if (!acc[trade.ticker]) {
      acc[trade.ticker] = [];
    }

    acc[trade.ticker].push(trade);
    return acc;
  }, {});

  return Object.keys(tradesByTicker).reduce<Record<string, OptionFlowTickerAggregate>>(
    (acc, ticker) => {
      const tickerTrades = tradesByTicker[ticker];
      const unusualTrades = tickerTrades.filter((trade) => isUnusual(trade));
      const sweepTrades = tickerTrades.filter((trade) => isSweep(trade));
      const splitTrades = tickerTrades.filter((trade) => isSplit(trade));
      const blockTrades = tickerTrades.filter((trade) => isBlock(trade));
      const goldenSweepTrades = tickerTrades.filter((trade) => isGoldenSweep(trade));
      const putFlowAggregate = calculateOptionFlowAggregate(tickerTrades, {
        flowSideBasis,
        putFlowBasis,
      });
      const baseOverview = calculateTickerOverview(tickerTrades, dailyVolumeMap[ticker]);
      const unusualOverview = calculateTickerOverview(unusualTrades, dailyVolumeMap[ticker]);
      const sweepOverview = calculateTickerOverview(sweepTrades, dailyVolumeMap[ticker]);
      const splitOverview = calculateTickerOverview(splitTrades, dailyVolumeMap[ticker]);
      const blockOverview = calculateTickerOverview(blockTrades, dailyVolumeMap[ticker]);
      const goldenSweepOverview = calculateTickerOverview(
        goldenSweepTrades,
        dailyVolumeMap[ticker],
      );
      const directional = calculateDirectionalFlowTotals(tickerTrades);
      const sentiment = calculateFlowSentimentBreakdown(directional.premium);
      const unusualDirectional = calculateDirectionalFlowTotals(unusualTrades);
      const sweepDirectional = calculateDirectionalFlowTotals(sweepTrades);
      const splitDirectional = calculateDirectionalFlowTotals(splitTrades);
      const blockDirectional = calculateDirectionalFlowTotals(blockTrades);
      const goldenSweepDirectional = calculateDirectionalFlowTotals(goldenSweepTrades);
      const totalContracts = baseOverview.contracts;
      const directionalCoveragePercent =
        totalContracts > 0 ? (directional.contracts.directionalTotal / totalContracts) * 100 : null;

      acc[ticker] = {
        ...baseOverview,
        ticker,
        tickerName: tickerTrades[0]?.tickerDetails?.name || '',
        marketCap: tickerTrades[0]?.marketCap || 0,
        tradeCount: tickerTrades.length,
        putFlow: toPercent(putFlowAggregate.putFlow),
        putCallRatio: putFlowAggregate.putCallRatio,
        putFlowVolume: toPercent(putFlowAggregate.putFlowVolume),
        putFlowPremium: toPercent(putFlowAggregate.putFlowPremium),
        putFlowBasis,
        flowSideBasis,
        sector: tickerTrades[0]?.tickerDetails?.sector || '',
        expiry: Math.round(baseOverview.expiry),
        unusualOtm: Math.round(unusualOverview.otm),
        unusualContracts: unusualOverview.contracts,
        unusualPremium: unusualOverview.premium,
        sweepOtm: Math.round(sweepOverview.otm),
        sweepContracts: sweepOverview.contracts,
        sweepPremium: sweepOverview.premium,
        splitOtm: Math.round(splitOverview.otm),
        splitContracts: splitOverview.contracts,
        splitPremium: splitOverview.premium,
        blockOtm: Math.round(blockOverview.otm),
        blockContracts: blockOverview.contracts,
        blockPremium: blockOverview.premium,
        goldenSweepOtm: Math.round(goldenSweepOverview.otm),
        goldenSweepContracts: goldenSweepOverview.contracts,
        goldenSweepPremium: goldenSweepOverview.premium,
        callVolume: putFlowAggregate.callVolume,
        callPremium: putFlowAggregate.callPremium,
        putVolume: putFlowAggregate.putVolume,
        putPremium: putFlowAggregate.putPremium,
        bullishPremium: directional.premium.bullish,
        bearishPremium: directional.premium.bearish,
        neutralPremium: directional.premium.neutral,
        sentimentScore: toPercent(sentiment.score),
        directionalContracts: directional.contracts.directionalTotal,
        neutralContracts: directional.contracts.neutral,
        directionalCoveragePercent,
        getEnrichmentAggregates: createEnrichmentAggregatesGetter(tickerTrades as EnrichedTrade[]),
        netTradeCount: directional.count.net,
        netContracts: directional.contracts.net,
        netPremium: directional.premium.net,
        netUnusualContracts: unusualDirectional.contracts.net,
        netUnusualPremium: unusualDirectional.premium.net,
        netSweepContracts: sweepDirectional.contracts.net,
        netSweepPremium: sweepDirectional.premium.net,
        netSplitContracts: splitDirectional.contracts.net,
        netSplitPremium: splitDirectional.premium.net,
        netBlockContracts: blockDirectional.contracts.net,
        netBlockPremium: blockDirectional.premium.net,
        netGoldenSweepContracts: goldenSweepDirectional.contracts.net,
        netGoldenSweepPremium: goldenSweepDirectional.premium.net,
        netPremiumShare: toPercent(calculateDirectionalPremiumShare(directional.premium)),
        hasTopPosition: tickerTrades.some((trade) => trade.isTopPosition),
        hasHighHeat: tickerTrades.some(
          (trade) => (trade.heatScore || 0) > HIGH_HEAT_SCORE_THRESHOLD,
        ),
        hasAboveAsk: tickerTrades.some((trade) => trade.tradeDirection === TradeDirection.AboveAsk),
        hasBelowBid: tickerTrades.some((trade) => trade.tradeDirection === TradeDirection.BelowBid),
      };

      return acc;
    },
    {},
  );
};

export const buildSectorOptionFlowAggregates = (
  trades: Trade[],
  options: SectorOptionFlowAggregateOptions = {},
) => {
  const { flowSideBasis, flowSideSelection, putFlowBasis } = getBasis(options);
  const basisTrades = filterTradesByFlowSideBasis(trades, flowSideSelection);
  const tradesBySector = basisTrades.reduce<Record<string, Trade[]>>((acc, trade) => {
    const originalSector = trade.tickerDetails?.sector;

    if (
      !originalSector ||
      (options.shouldIncludeSector && !options.shouldIncludeSector(originalSector, trade))
    ) {
      return acc;
    }

    const sector = mapCondensedSectors(originalSector);

    if (!acc[sector]) {
      acc[sector] = [];
    }

    acc[sector].push(trade);
    return acc;
  }, {});

  return Object.keys(tradesBySector).reduce<OptionFlowSectorAggregate[]>((acc, sector) => {
    const sectorTrades = tradesBySector[sector];
    const unusualTrades = sectorTrades.filter((trade) => isUnusual(trade));
    const sweepTrades = sectorTrades.filter((trade) => isSweep(trade));
    const splitTrades = sectorTrades.filter((trade) => isSplit(trade));
    const blockTrades = sectorTrades.filter((trade) => isBlock(trade));
    const goldenSweepTrades = sectorTrades.filter((trade) => isGoldenSweep(trade));
    const putFlowAggregate = calculateOptionFlowAggregate(sectorTrades, {
      flowSideBasis,
      putFlowBasis,
    });
    const baseOverview = calculateTickerOverview(sectorTrades);
    const unusualOverview = calculateTickerOverview(unusualTrades);
    const sweepOverview = calculateTickerOverview(sweepTrades);
    const splitOverview = calculateTickerOverview(splitTrades);
    const blockOverview = calculateTickerOverview(blockTrades);
    const goldenSweepOverview = calculateTickerOverview(goldenSweepTrades);
    const directional = calculateDirectionalFlowTotals(sectorTrades);
    const sentiment = calculateFlowSentimentBreakdown(directional.premium);

    acc.push({
      sector,
      putFlow: toPercent(putFlowAggregate.putFlow),
      expiry: Math.round(baseOverview.expiry),
      otm: Math.round(baseOverview.otm),
      contracts: baseOverview.contracts,
      premium: baseOverview.premium,
      unusualOtm: getRoundedOtm(unusualTrades),
      unusualContracts: unusualOverview.contracts,
      unusualPremium: unusualOverview.premium,
      sweepOtm: getRoundedOtm(sweepTrades),
      sweepContracts: sweepOverview.contracts,
      sweepPremium: sweepOverview.premium,
      splitOtm: getRoundedOtm(splitTrades),
      splitContracts: splitOverview.contracts,
      splitPremium: splitOverview.premium,
      blockOtm: getRoundedOtm(blockTrades),
      blockContracts: blockOverview.contracts,
      blockPremium: blockOverview.premium,
      goldenSweepOtm: getRoundedOtm(goldenSweepTrades),
      goldenSweepContracts: goldenSweepOverview.contracts,
      goldenSweepPremium: goldenSweepOverview.premium,
      bullishPremium: directional.premium.bullish,
      bearishPremium: directional.premium.bearish,
      neutralPremium: directional.premium.neutral,
      sentimentScore: toPercent(sentiment.score),
      netPremium: directional.premium.net,
      netPremiumShare: toPercent(calculateDirectionalPremiumShare(directional.premium)),
    });

    return acc;
  }, []);
};

export const calculateSectorPutFlow = (
  trades: Trade[],
  options: OptionFlowAggregateOptions = {},
) => {
  const aggregate = calculateOptionFlowAggregate(trades, options);
  return Math.round(aggregate.putFlow * 100);
};
